import { checkIsExtendItem } from '@/helpers/checkIsExtendItem'
import { checkIsShippedShieldItem } from '@/helpers/checkIsShippedShieldItem'
import { debug } from '@/services/Log'
import type { ProductType, ProductVariant } from '@/types/SearchAPI/SAPIProduct'
import type { BCCartType } from '@/types/BigCommerce/BCCart'
import type { StandardLineItem } from '@/types/ShopFront/CheckoutStandards'
import {
  arrivalWindow,
  getVariantBaseDate,
} from '@/components/Pdp/AddProductToCartForm/EstimatedArrival'
import { truthy } from '@/helpers/truthy'
import {
  type DeliveryQuote,
  skusToDeliveryQuotesMap,
} from '@/services/getDeliveryQuote'
import { getHydratedProductsBySkus } from '@/services/Product/getHydratedProductsBySkus'
import { isFurnitureVariant, isParcelVariant } from '@/helpers/shipmentTypeAnalyzer'

export const toStandardDiscounts = <T extends { id: string | number }>(discounts: readonly T[]) => (
  discounts.map((discount) => ({
    ...discount,
    id: String(discount.id),
  }))
)

export const isSearchable = ({
  sku,
  productId,
}: {
  sku: string
  productId: number | string
}) => !!productId && !!sku && !checkIsExtendItem({ sku }) && !checkIsShippedShieldItem({ sku })
const lineItemToSku = (p: { sku: string }) => p.sku

const getDeliveryDate = ({
  variant,
  skuDeliveryDates,
}:{
  variant: ProductVariant | undefined,
  skuDeliveryDates: Record<string, DeliveryQuote | null | undefined>,
}): {
  arrivalWindow: string
  completeArrivalWindow: string
  isBackOrder: boolean
} => {
  if (!variant) {
    return {
      arrivalWindow: '',
      completeArrivalWindow: '',
      isBackOrder: false,
    }
  }
  const deliveryQuote = skuDeliveryDates[variant.sku] || null
  const { date: baseDate, isBackOrder } = getVariantBaseDate(variant)
  return {
    arrivalWindow: arrivalWindow({
      variant,
      baseDate,
      maybeDeliveryQuote: deliveryQuote ? {
        ...deliveryQuote,
        vendorLeadTime: variant.LeadTimeDays,
      } : null,
    }),
    completeArrivalWindow: arrivalWindow({
      variant,
      baseDate,
      toDateTimeRange: true,
      maybeDeliveryQuote: deliveryQuote ? {
        ...deliveryQuote,
        vendorLeadTime: variant.LeadTimeDays,
      } : null,
    }),
    isBackOrder,
  }
}

const getproductURL = (product?: ProductType | undefined) => (targetSKU: string) => (
  `/p-${`${product?.name || 'product'}`.toLowerCase().replace(/([^a-z-])/g, ' ').trim().split(' ')[0]}-${product?.sku || 'P404'}?sku=${targetSKU}`
)

type ProductMapBySKU = Record<string, ProductType | undefined>
export const generateLineItemsFromBCCart = async (
  cart: BCCartType,
): Promise<StandardLineItem[]> => {
  debug('generateLineItemsFromBCCart START', { cart })
  const lineItems = [...cart.lineItems.physicalItems, ...cart.lineItems.digitalItems]
  const uniqueSkus = [...(new Set(
    lineItems
    // Filter out skus that are failing sku validation on product search
    // - extended warranty & shippedshield
      .filter(isSearchable)
      .map(lineItemToSku),
  ))]
  const [
    skusData,
  ] = await Promise.all([
    getHydratedProductsBySkus(uniqueSkus),
  ])
  debug('generateLineItemsFromBCCart skusData', { skusData })
  const itemMetadata = skusData.reduce<ProductMapBySKU>((acc, p) => {
    acc[p.id] = p
    acc[p.sku] = p
    p.variants.forEach((v) => {
      acc[v.sku] = p
    })
    return acc
  }, {})
  const parcelSkus = uniqueSkus.filter((sku) => (
    !!itemMetadata[sku]?.variants.some(isParcelVariant)
  ))
  // This is conditionally called on parcelSkus to avoid unnecessary API calls
  // that slow down the checkout process
  const skuDeliveryDates = await skusToDeliveryQuotesMap(parcelSkus)

  const ungroupedItems = lineItems.map((lineItem): StandardLineItem => {
    const product = itemMetadata[lineItem.sku]
    const variant = product?.variants?.find(({ sku }) => lineItem.sku === sku)
    return {
      ...lineItem,
      ...getDeliveryDate({
        variant,
        skuDeliveryDates,
      }),
      cost_price: product?.cost_price || 0,
      subtotalPrice: lineItem.listPrice,
      options: [...(lineItem.options || [])],
      discounts: [...toStandardDiscounts(lineItem.discounts)],
      couponAmount: (
        lineItem.discounts.reduce((acc, { discountedAmount }) => acc + discountedAmount, 0)
      ),
      originalName: lineItem.name,
      salePriceAfterDiscount: lineItem.salePrice,
      url: getproductURL(product)(lineItem.sku),
      imageUrl: (
        product?.variants?.find(({ sku }) => lineItem.sku === sku)?.images?.[0]?.url_square
        || lineItem?.imageUrl
      ),
      originalPrice: lineItem.originalPrice,
      isFurniture: !!variant && isFurnitureVariant(variant),
      isDoorstepEligible: variant?.isDoorstepEligible || false,
      isRug: variant?.isRug || false,
      isReturnable: truthy(variant?.Returnable),
      isDigital: truthy(product?.isDigital),
      isParcelItem: !!variant && isParcelVariant(variant),
      isExtendItem: false,
      overSizeCharge: 0,
      giftWrapping: null,
      isMutable: false,
      isFreeShipping: !!variant?.FreeShipping,
      freeShippingBadgeLabel: variant?.freeShippingBadgeLabel,
      currentProductId: variant?.product_id || lineItem?.productId || 0,
      currentVariantId: variant?.id || lineItem?.variantId || 0,
    }
  })

  // Sorting furniture to the end to match output from LineItemsFromCheckout
  const response = [...ungroupedItems].sort(
    (item1, item2) => (
      // eslint-disable-next-line no-nested-ternary
      (!item1.isFurniture && item2.isFurniture)
        ? -1
        : (item1.isFurniture && !item2.isFurniture)
          ? 1
          : 0
    ),
  )

  debug('generateLineItemsFromBCCart END', { response })
  return response
}

export type AugmentedLineItemsFromBCCart = ReturnType<typeof generateLineItemsFromBCCart>

export default generateLineItemsFromBCCart
